<template>
  <div>
    <!-- Row for Category Name and Add Subcategory Button -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1>{{ category.name }}</h1>
      <b-button variant="primary" @click="showAddSubcategoryModal = true">
        Add Subcategory
      </b-button>
    </div>
    
  

    <!-- Search Subcategory Input, Title, and Clear Button in the same row -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5>Search Subcategory</h5>
      <div class="d-flex">
        <b-form-input v-model="search" placeholder="Search subcategories..." class="mr-2" />
        <b-button :disabled="!search" @click="clearSearch">Clear</b-button>
      </div>
    </div>
    <b-col  cols="12" md="2" sm="2" class="my-1">
      <b-form-group label="Per Page:" label-for="per-page-select" class="mb-0">
    <b-form-select 
      id="per-page-select" 
      v-model="perPage" 
      :options="pageOptions" 
      size="sm"
    />
  </b-form-group>
    </b-col>

    <!-- Subcategory Table -->
    <b-table striped hover :items="filteredSubCategories" :fields="fields" :per-page="perPage"
      :current-page="currentPage">
      <template #cell(actions)="data">
        <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
          <template v-slot:button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="text-body align-middle mr-25" />
          </template>
          <!-- Edit Subcategory Option -->
          <b-dropdown-item @click="editSubcategory(data.item)">
            <feather-icon icon="EditIcon" class="mr-50" />
            <span>Edit</span>
          </b-dropdown-item>
          <!-- Delete Subcategory Option -->
          <b-dropdown-item @click="removeSubcategory(data.item)">
            <feather-icon icon="TrashIcon" class="mr-50" />
            <span>Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-col cols="12">
    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
    class="my-0" />
      <b-form-group>
        Showing 
        {{ totalRows > 0 ? (perPage * (currentPage - 1) + 1) : 0 }} 
        to 
        {{ totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0 }} 
        of 
        {{ totalRows > 0 ? totalRows : 0 }} 
        entries
      </b-form-group>
    </b-col>

    <!-- Add Subcategory Modal -->
    <b-modal id="modal-addsubcategory" v-model="showAddSubcategoryModal" title="Add Subcategory" hide-footer centered>
      <b-form @submit.prevent="addSubcategory">
        <b-form-group label="Subcategory Name" label-for="subcategoryName">
          <b-form-input id="subcategoryName" v-model="newSubcategoryName" placeholder="Enter subcategory name" />
        </b-form-group>
        <div class="modal-footer">
          <b-button variant="secondary" @click="showAddSubcategoryModal = false">
            Cancel
          </b-button>
          <b-button type="submit" variant="primary">
            <b-spinner small v-if="loading" class="mx-2" />Save
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Edit Subcategory Modal -->
    <b-modal id="modal-editsubcategory" v-model="showEditSubcategoryModal" title="Edit Subcategory" hide-footer
      centered>
      <b-form @submit.prevent="updateSubcategory">
        <b-form-group label="Subcategory Name" label-for="editSubcategoryName">
          <b-form-input id="editSubcategoryName" v-model="editSubcategoryName"
            placeholder="Enter new subcategory name" />
        </b-form-group>
        <div class="modal-footer">
          <b-button variant="secondary" @click="showEditSubcategoryModal = false">Cancel</b-button>
          <b-button type="submit" variant="primary">Save</b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { customError, customSwal } from "@/libs/customFn";
import {
  BRow,
  BCol,
  BTable,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BDropdown,
  BFormSelect,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import Swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BPagination,
  },
  props: ["id"], // 'id' is passed via route params
  data() {
    return {
      pageOptions: [3, 5, 10, 20, 50], // Options for per page dropdown
    currentPage: 1,
    totalRows: 0,
      perPage: 5, // Number of items per page
      category: {}, // Store the selected category data
      subCategories: [], // Store the list of subcategories
      search: "", // Filter search value
      showAddSubcategoryModal: false,
      showEditSubcategoryModal: false,
      newSubcategoryName: "", // Name of the new subcategory
      editSubcategoryName: "", // Name of the subcategory to edit
      currentEditingSubcategory: null, // Currently editing subcategory
      fields: [
        { key: "name", label: "Subcategory Name" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      categoryNamesMap: {}, // Store mapping of category IDs to names
    };
  },
  computed: {
    // Filter the subcategories based on the search input
    filteredSubCategories() {
      let subList = this.subCategories.filter((subcategory) =>
      subcategory.name.toLowerCase().includes(this.search.toLowerCase()));

      this.totalRows = subList.length;
      this.currentPage = 1;
      return subList;
    },
  },
  created() {
    this.fetchCategoryDetails();
  },
  methods: {
    ...mapActions("itemcategoryModule", [
      "getItemCategoryListAction",
      "removeItemCategoryAction",
      "addItemCategoryAction",
      "updateItemCategoryAction",
      "getItemSubCategoryListAction",
      "getItemCategoryAction",
    ]),
    async fetchCategoryDetails() {
      this.category = await this.getItemCategoryAction(this.$route.params.id);
      this.subCategories = await this.getItemSubCategoryListAction(this.$route.params.id);
      console.log("🚀 ~ fetchCategoryDetails ~ categoryData:", this.subCategories)

    },
    addSubcategory() {
      if (this.newSubcategoryName.trim()) {
        // Create the payload for the new subcategory
        const payload = {
          name: this.newSubcategoryName,
          parentId: this.$route.params.id, // Use the current category as parentId
          isSub: true, // Indicate that it's a subcategory
        };

        // Start the Swal loader
        this.$swal({
          title: "Adding Subcategory...",
          text: "Please wait",
          allowEscapeKey: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading(); // Show loading spinner
          },
        });

        // Call the Vuex action to add the subcategory
        this.$store
          .dispatch("itemcategoryModule/addItemCategoryAction", payload)
          .then(() => {
            // Show success message
            this.$swal({
              icon: "success",
              title: "Subcategory Added",
              text: "The subcategory has been successfully added!",
              timer: 2000,
              showConfirmButton: false,
            });

            this.fetchCategoryDetails();

            // Clear input and close modal
            this.newSubcategoryName = "";
            this.showAddSubcategoryModal = false;
          })
          .catch((error) => {
            // Show error message
            this.$swal({
              icon: "error",
              title: "Error",
              text: `Failed to add subcategory: ${error.response?.data?.message || error.message
                }`,
            });
          });
      }
    },

    editSubcategory(subcategory) {
      this.currentEditingSubcategory = subcategory;
      this.editSubcategoryName = subcategory.name;
      this.showEditSubcategoryModal = true;
    },
    async updateSubcategory() {
      // Update the subcategory name and close the edit modal
      if (this.currentEditingSubcategory) {
        this.currentEditingSubcategory.name = this.editSubcategoryName;
        const payload = {
          name: this.editSubcategoryName.trim(), // Use the updated name
          parentId: this.currentEditingSubcategory.parentId, // Retain the parent ID
          isSub: true, // Indicate it's a subcategory
          id: this.currentEditingSubcategory.id, // Include the subcategory ID
        };
        await this.updateItemCategoryAction(payload)
        .then((response) => {
          if(response.data.success) {
            customSwal('Sub category has been updated')
          }else{
            customError(response.data)
          }
          
          
      })
        this.showEditSubcategoryModal = false;
        this.currentEditingSubcategory = null;
      }
    },
    removeSubcategory(subcategory) {
      // Show confirmation dialog
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          // Dispatch the delete action to the Vuex store
          this.$store
            .dispatch(
              "itemcategoryModule/removeItemCategoryAction",
              subcategory.id
            )
            .then(() => {
              this.$swal({
                icon: "success",
                title: "Deleted!",
                text: "The subcategory has been deleted.",
                customClass: {
                  confirmButton: "btn btn-success",
                },
              });
              // Remove the subcategory from the local list
              this.subCategories = this.subCategories.filter(
                (sub) => sub.id !== subcategory.id
              );
            })
            .catch((error) => {
              this.$swal({
                title: "Delete failed!",
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-success",
                },
              });
            });
        } else if (result.dismiss === "cancel") {
          this.$swal({
            title: "Cancelled",
            text: "Your subcategory is safe :)",
            icon: "error",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
        }
      });
    },
    clearSearch() {
      this.search = "";
    },
  },
  mounted() {
    this.fetchCategoryDetails();
  },
};
</script>